/* eslint-disable @next/next/no-img-element */
import { ComponentProps, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  theme,
} from "antd";
import {
  ExceptionOutlined,
  LoadingOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { ResourceAccessRadioButtons } from "src/components/ResourceAccessRadioButtons";
import { useApplicationContext } from "src/hooks/useApplication";
import { useThumbnail } from "src/hooks/useThumbnail";
import { Duration } from "src/utils/date";
import { Flex } from "src/components/Flex";
import { CollapsibleRow } from "../entity/[entityId]/entity/components/CollapsibleRow";
import {
  DEFAULT_COLUMN_COUNT,
  DEFAULT_ROW_HEIGHT,
  DEFAULT_SPACING,
} from "./DashboardViewer";
import { DashboardController } from "./DashboardController";

export function DashboardEditor(
  props: Pick<
    { onClose(): void; tags: string[] } & ComponentProps<
      typeof DashboardController
    >,
    | "description"
    | "thumbnail"
    | "extra"
    | "name"
    | "onClose"
    | "onSave"
    | "scope"
    | "searchState"
    | "setDescription"
    | "setExtra"
    | "setName"
    | "setScope"
    | "tags"
  >
) {
  const { token } = theme.useToken();
  const [name, setName] = useState(props.name);
  const [description, setDescription] = useState(props.description);
  const [scope, setScope] = useState(props.scope);
  const [spacing, setSpacing] = useState(
    props.extra?.spacing === undefined ? DEFAULT_SPACING : props.extra.spacing
  );
  const [colorTheme, setColorTheme] = useState(
    props.extra?.colorTheme === "alternate" ? "alternate" : "standard"
  );
  const [columnCount, setColumnCount] = useState(
    props.extra?.columnCount === undefined
      ? DEFAULT_COLUMN_COUNT
      : props.extra.columnCount
  );
  const [rowHeight, setRowHeight] = useState(
    props.extra?.rowHeight === undefined
      ? DEFAULT_ROW_HEIGHT
      : props.extra.rowHeight
  );
  const [updateState, setUpdateState] = useState(false);
  const [updateThumbnail, setUpdateThumbnail] = useState(false);
  const [tags, setTags] = useState(
    props.extra?.tags && Array.isArray(props.extra.tags) ? props.extra.tags : []
  );
  const [systemName, setSystemName] = useState(props.extra?.systemName || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const app = useApplicationContext();
  const canEditShare =
    app.isSuperAdmin || (app.isOrgAdmin && scope !== "global");

  const { thumbnail, error: captureError } = useThumbnail(
    document.querySelector("#dashboard-scroll-parent")
  );
  return (
    <Modal
      width={800}
      open
      closable={false}
      maskClosable={false}
      okButtonProps={{ loading: saving }}
      okText={saving ? "Saving" : "Save"}
      cancelButtonProps={{ disabled: saving }}
      onCancel={() => props.onClose()}
      onOk={() => {
        setSaving(true);
        props.setName(name);
        props.setDescription(description);
        props.setScope(scope);
        props.setExtra({
          colorTheme,
          columnCount,
          defaultDateRange: updateState
            ? { duration: props.searchState.duration as Duration }
            : props.extra.defaultDateRange,
          defaultFilters: updateState
            ? props.searchState.filters
            : props.extra.defaultFilters,
          rowHeight,
          spacing,
          style: {},
          systemName,
          tags,
        });
        props
          .onSave({
            colorTheme,
            columnCount,
            defaultDateRange: updateState
              ? { duration: props.searchState.duration as Duration }
              : props.extra.defaultDateRange,
            defaultFilters: updateState
              ? props.searchState.filters
              : props.extra.defaultFilters,
            name,
            description,
            rowHeight,
            scope,
            spacing,
            style: {},
            systemName,
            tags,
            thumbnail: updateThumbnail ? thumbnail : props.thumbnail,
          })
          .then(() => props.onClose())
          .catch(() => {
            setSaving(false);
            setError("There was a problem, please try again.");
          });
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            textAlign: "center",
            marginBottom: 8,
          }}
        >
          Board Settings
        </div>

        {/* Basic Information Section */}
        <div
          style={{
            padding: 12,
            background: token.colorBgElevated,
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              <span>
                NAME{" "}
                <span
                  style={{ fontWeight: 400, color: token.colorTextSecondary }}
                >
                  (required)
                </span>
              </span>
            </div>
            <Input
              size="large"
              disabled={saving}
              placeholder="Board Name"
              onChange={(e) => setName(e.target.value)}
              value={name}
            />
          </div>

          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 4,
                paddingTop: 12,
              }}
            >
              DESCRIPTION
            </div>
            <Input.TextArea
              size="large"
              disabled={saving}
              placeholder="Description"
              onChange={(e) => setDescription(e.target.value)}
              value={description}
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </div>
        </div>

        {/* Tags Section */}
        <div
          style={{
            padding: 12,
            background: token.colorBgElevated,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            TAGS / CATEGORIES
          </div>
          <Select
            size="large"
            disabled={saving}
            mode="tags"
            style={{ width: "100%" }}
            value={tags}
            onChange={setTags}
            options={[...new Set(props.tags)].map((tag) => ({
              label: tag,
              value: tag,
            }))}
            notFoundContent={
              <Flex
                centered
                gap={8}
                style={{ padding: 16, color: token.colorTextSecondary }}
              >
                <span>Type to create tags</span>
                <TagOutlined />
              </Flex>
            }
          />
        </div>

        {/* Settings Section */}
        <div
          style={{
            padding: 12,
            background: token.colorBgElevated,
          }}
        >
          {canEditShare && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                VISIBILITY
              </div>
              <ResourceAccessRadioButtons
                isAtLeastSuper={app.isSuperAdmin}
                type="board"
                disabled={saving}
                value={scope || "private"}
                onChange={(e) => setScope(e.target.value)}
              />
            </div>
          )}
        </div>
        <div style={{ padding: 12, background: token.colorBgElevated }}>
          <Checkbox
            checked={updateState}
            onChange={(e) => setUpdateState(e.target.checked)}
          >
            Save current global date and filters
          </Checkbox>
        </div>
        {/* Advanced Section */}
        <CollapsibleRow
          openOnPreviewClick
          preview={
            <span
              style={{
                color: token.colorTextTertiary,
                display: "inline-block",
                paddingTop: 5,
                fontSize: 12,
                cursor: "default",
              }}
            >
              Advanced Settings{" "}
              <span style={{ color: token.colorTextQuaternary }}>
                ({updateState ? "saving global query" : "not saving query"})
              </span>
            </span>
          }
          content={
            <div
              style={{
                padding: 12,
                background: token.colorBgElevated,
              }}
            >
              <Flex
                direction="column"
                gap={16}
                style={{ width: "100%", alignItems: "flex-start" }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    Color Theme
                  </div>
                  <Radio.Group
                    disabled={saving}
                    value={colorTheme}
                    onChange={(e) => setColorTheme(e.target.value)}
                  >
                    <Radio value="standard">Standard</Radio>
                    <Radio value="alternate">Alternate</Radio>
                  </Radio.Group>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 12,
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    Layout Options
                  </div>
                  <Flex start gap={16} style={{ alignItems: "flex-end" }}>
                    <div>
                      <div style={{ fontWeight: 200 }}>columns</div>
                      <InputNumber
                        disabled={saving}
                        value={columnCount}
                        onChange={(value) =>
                          setColumnCount(Math.max(0, value || 0))
                        }
                      />
                    </div>
                    <div>
                      <div style={{ fontWeight: 200 }}>row height (px)</div>
                      <InputNumber
                        disabled={saving}
                        value={rowHeight}
                        onChange={(value) =>
                          setRowHeight(Math.max(0, value || 0))
                        }
                      />
                    </div>
                    <div>
                      <div style={{ fontWeight: 200 }}>spacing (px)</div>
                      <InputNumber
                        disabled={saving}
                        value={spacing}
                        onChange={(value) =>
                          setSpacing(Math.max(0, value || 0))
                        }
                      />
                    </div>
                    <Button
                      type="link"
                      onClick={() => {
                        setSpacing(DEFAULT_SPACING);
                        setColumnCount(DEFAULT_COLUMN_COUNT);
                        setRowHeight(DEFAULT_ROW_HEIGHT);
                      }}
                    >
                      reset to defaults
                    </Button>
                  </Flex>
                </div>

                <div>
                  <Checkbox
                    checked={updateThumbnail}
                    onChange={(e) => setUpdateThumbnail(e.target.checked)}
                  >
                    Update thumbnail
                  </Checkbox>
                  <div>
                    <Flex gap={16} style={{ marginTop: 16 }}>
                      {thumbnail ? (
                        <div
                          style={{
                            height: 90,
                            width: 160,
                            overflow: "hidden",
                            border: `solid 1px ${token.colorBorderSecondary}`,
                            borderRadius: 4,
                          }}
                        >
                          <img
                            alt="New thumbnail"
                            src={thumbnail}
                            style={{ display: "block", width: "100%" }}
                          />
                        </div>
                      ) : captureError ? (
                        <ExceptionOutlined />
                      ) : (
                        <LoadingOutlined />
                      )}
                    </Flex>
                  </div>
                </div>

                {app.isSuperAdmin && (
                  <div style={{ width: "100%" }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        marginBottom: 16,
                      }}
                    >
                      SYSTEM NAME
                    </div>
                    <Input
                      size="large"
                      disabled={saving}
                      placeholder="System Name"
                      onChange={(e) => setSystemName(e.target.value)}
                      value={systemName}
                    />
                  </div>
                )}
              </Flex>
            </div>
          }
        />
        {error && <Alert type="error" message={error} />}
      </div>
    </Modal>
  );
}
